<script setup>
// Import functions
// Import components
import DialogFooter from 'components/UI/Dialogs/DialogFooter.vue'
import DialogFooterActions from 'components/UI/Dialogs/DialogFooterActions.vue'
import { format } from 'quasar'

const { t } = useI18n({ useScope: 'global' })

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  message: {
    type: String,
    required: true,
  },
  confirmLabel: {
    type: String,
    default: null,
  },
  tableColumns: {
    type: Array,
    default: () => [],
  },
  tableRows: {
    type: Array,
    default: () => [],
  },
})

// Quasar Dialog
defineEmits([...useDialogPluginComponent.emits])
const { dialogRef, onDialogHide, onDialogOK, onDialogCancel } = useDialogPluginComponent()

// Constants declarations
const { capitalize } = format
const $q = useQuasar()
const attrs = useAttrs()

// Refs
const maximizedToggle = ref(false)

// Computed properties
const maximized = computed(() => {
  return maximizedToggle.value || $q.screen.lt.md
})

const dialogWidthClass = computed(() => {
  return props.tableRows.length > 0 ? 'dialog-width-66' : 'dialog-width-33'
})

const tableValue = (row, column) => {
  const value = typeof column.field === 'function' ? column.field(row) : row[column.field]
  return column.format ? column.format(value, row) : value
}

const footerButtons = computed(() => [
  {
    action: onDialogOK,
    show: true,
    customProps: { label: props.confirmLabel || t('Globals.Yes'), color: 'primary' },
  },
])
</script>

<template>
  <q-dialog :maximized="maximized" ref="dialogRef" @hide="onDialogHide" no-esc-dismiss no-backdrop-dismiss :class="{ 'mobile-dialog': $q.screen.lt.md }">
    <q-card ref="innerDialog" :class="!maximized ? dialogWidthClass : ''" class="relative-position">
      <div id="products" class="relative-position">
        <q-bar class="bg-primary q-py-lg q-px-md text-h5 text-white">
          <span class="non-selectable text-h6">
            {{ title }}
          </span>
        </q-bar>

        <q-form @submit.prevent.stop="onDialogOK" greedy ref="formValidator" class="relative-position">
          <q-separator />
          <q-card-section
            :class="!maximized ? `dialog-normal${$q.screen.lt.md ? '-mobile' : ''}` : `dialog-maximized${$q.screen.lt.md ? '-mobile' : ''}`"
            class="scroll q-pa-none relative-position"
          >
            <div class="row">
              <div class="col q-pa-md" v-html="message"></div>
            </div>
            <div class="row q-px-md q-pb-md" v-if="tableRows.length > 0 && tableColumns.length > 0">
              <div class="col-12">
                <q-markup-table dense flat bordered>
                  <thead>
                    <tr>
                      <th v-for="column in tableColumns" :key="column.name" class="text-left">
                        {{ column.label }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, rowIndex) in tableRows" :key="row.id ?? row.presence_id ?? rowIndex">
                      <td v-for="column in tableColumns" :key="column.name">
                        {{ tableValue(row, column) ?? '' }}
                      </td>
                    </tr>
                  </tbody>
                </q-markup-table>
              </div>
            </div>
          </q-card-section>
          <q-card-actions align="right">
            <dialog-footer>
              <template #footer-buttons>
                <dialog-footer-actions :cancel-action="onDialogCancel" :actions="footerButtons" />
              </template>
            </dialog-footer>
          </q-card-actions>
        </q-form>
      </div>
    </q-card>
  </q-dialog>
</template>

<style scoped></style>
